/**
 * Lightweight usage analytics. Buffers events and flushes every 10s or on page unload.
 * Call `initAnalytics(user, { isSimulatedView })` once after auth, then
 * `trackEvent(name, props)`.
 *
 * RW-2379 — every event also carries `isInternal` + `accessLevel`. Internal
 * traffic is TAGGED, never dropped: a full scan on 2026-09-11 found 87.5% of
 * all 28,817 events came from the 5 elevated accounts (83.3% from one
 * super_admin), so a reader that does not filter is mostly measuring us. The
 * tag is deliberately reversible — the events stay available for debugging.
 *
 * RW-2351 — `isSimulatedView` means "an admin or staff member is deliberately
 * viewing the app AS someone else" (the App.js view switcher, gated to real
 * admins, or onboarding's preview-as-hire). While that is true, layer 2 (the
 * Firestore `analytics` collection) drops every event so simulated browsing
 * does not read as adoption. Layer 1 (GA4) is unaffected.
 *
 * It was called `isAdminView`, which was actively dangerous: ViewModeContext
 * exports its OWN `isAdminView` meaning `viewMode === 'admin'`, and viewMode
 * DEFAULTS to 'admin' for every user. Anyone making the two names agree would
 * have gated out the entire user base and silently ended all Firestore
 * analytics. The names must stay distinct. src/lib/analytics.viewGate.test.js
 * pins both the behaviour and the App.js call site.
 */
import { db } from '../firebase';
import { isElevated } from './roles';
import { addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { withTenant } from '../services/data';

let currentUser = null;
let isSimulatedView = false;
// RW-2379: the caller's coarse access level ('super_admin' | 'admin' |
// 'member'), or null while it is genuinely unknown. Never defaults to a
// value — see the isInternal note in trackEvent.
let currentAccessLevel = null;
let buffer = [];
let flushTimer = null;
let sessionId = null;
let dailyCount = 0;
let dailyCountKey = null;
let lastEvent = { name: null, props: null, at: 0 };

function genId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getOrCreateSessionId() {
  if (sessionId) return sessionId;
  try {
    sessionId = sessionStorage.getItem('reve_session_id');
    if (!sessionId) {
      sessionId = genId();
      sessionStorage.setItem('reve_session_id', sessionId);
    }
  } catch { sessionId = genId(); }
  return sessionId;
}

function getDailyCount() {
  const today = new Date().toISOString().slice(0, 10);
  if (dailyCountKey !== today) {
    dailyCountKey = today;
    try {
      const raw = localStorage.getItem('reve_analytics_daily');
      if (raw) {
        const { date, count } = JSON.parse(raw);
        dailyCount = date === today ? count : 0;
      } else dailyCount = 0;
    } catch { dailyCount = 0; }
  }
  return dailyCount;
}
function bumpDailyCount() {
  dailyCount = getDailyCount() + 1;
  try { localStorage.setItem('reve_analytics_daily', JSON.stringify({ date: dailyCountKey, count: dailyCount })); } catch {}
}

export function initAnalytics(user, opts = {}) {
  currentUser = user;
  isSimulatedView = !!opts.isSimulatedView;
  // RW-2379: TAG, never gate. Internal traffic stays in the collection so it
  // remains available for debugging; every READ decides whether to count it.
  currentAccessLevel = typeof opts.accessLevel === 'string' && opts.accessLevel
    ? opts.accessLevel
    : null;
  if (!user?.uid) return;
  getOrCreateSessionId();

  // Flush on tab close / visibility change
  if (typeof window !== 'undefined' && !window.__reveAnalyticsInit) {
    window.__reveAnalyticsInit = true;
    window.addEventListener('beforeunload', () => flush(true));
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(true); });
  }
  // Periodic flush every 10 seconds
  if (!flushTimer) {
    flushTimer = setInterval(flush, 10000);
  }
  // Write/refresh the session doc
  try {
    setDoc(doc(db, 'sessions', getOrCreateSessionId()), withTenant('sessions', {
      userId: user.uid,
      displayName: user.displayName || '',
      startedAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    }), { merge: true });
  } catch {}
}

export function trackEvent(eventName, properties = {}) {
  if (!currentUser?.uid) return;
  if (isSimulatedView) return; // RW-2351: admin/staff viewing AS someone else
  if (currentUser._seedData) return;
  if (getDailyCount() >= 1000) return; // Cap

  // Debounce duplicate rapid events
  const propsKey = JSON.stringify(properties || {});
  const now = Date.now();
  if (lastEvent.name === eventName && lastEvent.props === propsKey && now - lastEvent.at < 2000) return;
  lastEvent = { name: eventName, props: propsKey, at: now };

  buffer.push({
    event: eventName,
    userId: currentUser.uid,
    displayName: currentUser.displayName || '',
    email: currentUser.email || '',
    role: currentUser.role || '',
    // RW-2379: `role` above is roles[0] (a LABEL), not privilege. These two
    // carry privilege, so a reader never has to re-derive it from `role`.
    // isInternal is null — not false — when the access level never arrived;
    // a false there would be a claim this was agent traffic.
    accessLevel: currentAccessLevel || '',
    isInternal: currentAccessLevel ? isElevated(currentAccessLevel) : null,
    office: currentUser.office || currentUser.primaryOffice || '',
    page: typeof window !== 'undefined' ? window.location.pathname : '',
    properties: properties || {},
    sessionId: getOrCreateSessionId(),
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    _ts: now,
  });
  bumpDailyCount();
  if (buffer.length >= 25) flush();
}

async function flush(sync = false) {
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  try {
    // Simple parallel writes — the firebase SDK batches under the hood.
    // For sync (beforeunload), we fire-and-forget; the browser may kill the tab before the promise resolves.
    await Promise.all(batch.map((ev) => addDoc(collection(db, 'analytics'), withTenant('analytics', {
      ...ev,
      timestamp: serverTimestamp(),
    }))));
  } catch (e) {
    // RW-477: permission-denied is expected during auth transitions (sign-out, token refresh, beforeunload) — silence it.
    if (e?.code !== 'permission-denied') {
      console.warn('analytics flush failed', e?.message);
    }
    // Re-buffer on failure (but cap to avoid runaway)
    if (buffer.length < 100) buffer.unshift(...batch);
  }
}
